import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CollapseState } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';

import { IconButtonComponent } from '../../common/button/components/icon-button/icon-button.component';
import { IconComponent } from '../../common/icon/icon.component';
import { HotkeyPressEvent, HotkeysService } from '../../core/a11y/hotkeys.service';
import { KeyCode } from '../../core/keycode';
import { ScrollableDirective } from '../../core/overlay/scroll/scrollable.directive';
import { DialogService } from '../../dialogs/dialogs.service';
import { SidebarAnalytics, SidebarHotkeyAction } from '../sidebar.analytics';
import { SidebarInternalService } from './sidebar-internal.service';

export const collapseHorizontal = trigger('collapseHorizontal', [
  state(
    'collapsed',
    style({
      width: '48px',
      minWidth: '48px',
    }),
  ),
  state(
    'normal',
    style({
      width: '*',
      minWidth: '*',
    }),
  ),
  // immediate change state from none state to collapsed
  transition('void <=> collapsed', animate('0ms')),
  transition('* <=> collapsed', animate('200ms cubic-bezier(0.16, 1, 0.3, 1)')),
  transition('collapsed <=> normal', animate('200ms cubic-bezier(0.16, 1, 0.3, 1)')),
]);

@Component({
  selector: 'rt-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.less'],
  providers: [SidebarInternalService],
  animations: [collapseHorizontal],
  standalone: true,
  imports: [ScrollableDirective, IconButtonComponent, TranslatePipe, IconComponent],
})
export class SidebarComponent implements OnInit {
  @Input() public hasExpander: boolean = true;
  @Input() public isExpanded: boolean = true;
  @Input() public enableExpanderShortcuts: boolean = true;
  @Input() public hasBorderRadius: boolean = true;
  @Input() public size: 'default' | 'large' = 'default';

  @Output() public expanded: EventEmitter<boolean> = new EventEmitter();
  @Output() public animationEvent: EventEmitter<AnimationEvent> = new EventEmitter();

  public isMinimized: boolean;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private sidebarInternalService: SidebarInternalService,
    private hotkeysService: HotkeysService,
    private dialogService: DialogService,
    private sidebarAnalytics: SidebarAnalytics,
  ) {}

  public ngOnInit(): void {
    this.isMinimized = !this.isExpanded;
    this.sidebarInternalService.sidebarMinimized.next(this.isMinimized);

    if (this.hasExpander && this.enableExpanderShortcuts) {
      merge(
        this.hotkeysService.shortcut([KeyCode.BracketLeft], true, false),
        this.hotkeysService.shortcut([KeyCode.BracketRight], true, false),
      )
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe((event: HotkeyPressEvent) => {
          this.handleHotkeyEvent(event);
        });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isExpanded?.currentValue) {
      this.isMinimized = !this.isExpanded;
      this.sidebarInternalService.sidebarMinimized.next(this.isMinimized);
    }
  }

  public get collapseState(): CollapseState {
    return this.isMinimized ? CollapseState.Collapsed : CollapseState.Normal;
  }

  public toggleSidebar(isMinimized: boolean): void {
    this.isMinimized = isMinimized;
    this.sidebarInternalService.sidebarMinimized.next(isMinimized);
    this.expanded.emit(!isMinimized);
    this.cdRef.detectChanges();
  }

  public handleHotkeyEvent(event: HotkeyPressEvent): void {
    if (this.dialogService.hasDialogsOpen()) return;

    switch (event.keys[0]) {
      case 'BracketRight':
        if (this.isMinimized) {
          this.sidebarAnalytics.trackSidebarHotkeyAction(SidebarHotkeyAction.Open);
          this.toggleSidebar(false);
        }
        break;
      case 'BracketLeft':
        if (!this.isMinimized) {
          this.sidebarAnalytics.trackSidebarHotkeyAction(SidebarHotkeyAction.Close);
          this.toggleSidebar(true);
        }
        break;
      default:
        return;
    }
  }
}
